import { CircleMinus, CirclePlus } from 'lucide-react-native';

import { POINT_BUTTON_TONE } from './pointButtonTone';

import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type PointButtonProps = {
  /** `add` puts a point into the reply; `remove` takes one out. */
  kind: 'add' | 'remove';
  /** Spoken in full, naming the point, since the button shows only an icon. */
  label: string;
  onPress: () => void;
  testID: string;
};

/**
 * The one control for changing what the reply says: a plus to put a point in,
 * a minus to take it out. Every point outside the reply carries the plus,
 * whether it was removed or only suggested, and every point inside carries
 * the minus, so the pair is learned once.
 *
 * The icon shows no word, so the label names the action and the point for
 * screen readers. The plus is the brand green and the minus the brand orange,
 * so adding reads as positive and taking out as a caution without the alarm
 * of error red, and the two differ by shape before color. Orange is
 * `tertiary-700`, not the brand `tertiary-500`, which sits beside the warning
 * hue and is too light on the card in light mode. Never a trash can or a
 * cross: those read as permanent deletion or as closing something.
 */
export function PointButton({ kind, label, onPress, testID }: PointButtonProps) {
  const isAdd = kind === 'add';

  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      className="h-12 w-12 items-center justify-center rounded-full web:cursor-pointer data-[hover=true]:bg-background-50"
      {...getAccessibilityProps({ role: 'button', label })}>
      <Icon as={isAdd ? CirclePlus : CircleMinus} size="xl" className={POINT_BUTTON_TONE[kind]} />
    </Pressable>
  );
}
